// Centralized color theme system with tenant-specific customization

import { isDechraFn, isShellFn } from "../layout/chatSidebar";

export const ChartColors = {
  desktop: [
    "#2A5354",
    "#54A7A8",
    "#4A2739",
    "#954F72",
    "#6D6604",
    "#D9CC08",
    "#012548",
    "#034A90",
  ],
  mobile: [
    "#54A7A8",
    "#3F7D7E",
    "#2A5354",
    "#0088FE",
    "#00C49F",
    "#FFBB28",
    "#FF8042",
    "#8884D8",
    "#82CA9D",
  ],
};

// 2. Theme types
export type ThemeMode = "light" | "dark";

export interface ThemeColors {
  // basics
  background: string;
  text: string;
  border: string;
  fontFamily: string;

  // interactivity
  buttonHover: string;

  // selects & forms
  optionsBg: string;
  optionsHover: string;
  convoStarterBg: string;
  convoStarterHover: string;
  placeholder: string;
  svgFill: string;
  multiValueBg: string;
  optionHoverBg: string;
  removeValueBg: string;
  sidebarBg: string;
  inputErrorBorder: string;

  // disabled
  disabledBg: string;
  disabledText: string;
  disabledPlaceholder: string;
  disabledSvgFill: string;

  // charts
  chartColors: {
    desktop: string[];
    mobile: string[];
  };

  // chat buttons
  chatBTNBackground: string;
  chatBTNHover: string;
  chatBTNDisabled: string;
}

// 3. Per‐tenant theme sets
export type TenantKey = string; // e.g. company ID or username

export interface ThemeSet {
  light: ThemeColors;
  dark: ThemeColors;
}

// — define your default themeSet here
const defaultTheme: ThemeSet = {
  light: {
    background: "#FFFFFF",
    text: "#000000",
    border: "#CCCCCC",
    fontFamily: `"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`,
    buttonHover: "#E2E8F0",
    optionsBg: "#F2F2F2",
    sidebarBg: "#F2F2F2",
    optionsHover: "#E2E8F0",
    convoStarterBg: "#F2F2F2",
    convoStarterHover: "#E2E8F0",
    placeholder: "#888888",
    svgFill: "#555555",
    multiValueBg: "#E6E6E6",
    optionHoverBg: "#F2F2F2",
    removeValueBg: "#D9D9D9",
    disabledBg: "#F0F0F0",
    disabledText: "#A0A0A0",
    disabledPlaceholder: "#BBBBBB",
    disabledSvgFill: "#BBBBBB",
    chartColors: ChartColors,
    inputErrorBorder: "#ff6b6b",
    chatBTNBackground: "#E2E8F0", // convoStarterHover
    chatBTNHover: "#F2F2F2", // optionHoverBg
    chatBTNDisabled: "#F0F0F0", // disabledBg

  },
  dark: {
    background: "#191919",
    text: "#FFFFFF",
    border: "#343434",
    fontFamily: `"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`,
    buttonHover: "#3B9161",
    optionsBg: "#2D2D2D",
    sidebarBg: "#191919",
    optionsHover: "#3B9161",
    convoStarterBg: "#2D2D2D",
    convoStarterHover: "#3B9161",
    placeholder: "#797A7B",
    svgFill: "#797A7B",
    multiValueBg: "#333333",
    optionHoverBg: "#444444",
    removeValueBg: "#555555",
    disabledBg: "#2A2A2A",
    disabledText: "#797A7B",
    disabledPlaceholder: "#4A4A4A",
    disabledSvgFill: "#4A4A4A",
    chartColors: ChartColors,
    inputErrorBorder: "#ff6b6b",
    chatBTNBackground: "#3B9161", // convoStarterHover
    chatBTNHover: "#444444", // optionHoverBg
    chatBTNDisabled: "#2A2A2A", // disabledBg
  },
};

// — now list out each "tenant" you support, importing or inlining their custom palettes:
export const TenantThemes: Record<TenantKey, ThemeSet> = {
  default: defaultTheme,
  dechra: {
    light: {
      ...defaultTheme.light,
      background: "#FFFFFF", // Dechra White
      text: "#333333", // Dechra Dark Gray
      border: "#EDEDED", // Dechra Gray
      fontFamily: `"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`,
      buttonHover: "#A7C6ED", // Dechra Light Blue
      optionsBg: "#F5F5F5", // Light neutral background
      sidebarBg: "#F5F5F5", // Light neutral background
      optionsHover: "#A7C6ED", // Dechra Light Blue
      convoStarterBg: "#F5F5F5", // Light neutral background
      convoStarterHover: "#A7C6ED", // Dechra Light Blue
      placeholder: "#888888", // Neutral placeholder text
      svgFill: "#555555", // Icon color
      multiValueBg: "#E6E6E6", // Selected item background
      optionHoverBg: "#F5F5F5", // Hover background
      removeValueBg: "#D9D9D9", // Remove button background
      disabledBg: "#F0F0F0", // Disabled background
      disabledText: "#A0A0A0", // Disabled text
      disabledPlaceholder: "#BBBBBB", // Disabled placeholder
      disabledSvgFill: "#BBBBBB",
      chatBTNBackground: "#A7C6ED", // convoStarterHover
      chatBTNHover: "#F5F5F5", // optionHoverBg
      chatBTNDisabled: "#F0F0F0", // disabledBg
      chartColors: {
        desktop: [
          "#003B71",
          "#A3DAFF",
          "#045252",
          "#67E6BB",
          "#4E065C",
          "#BB80D1",
        ],
        mobile: [
          "#003B71",
          "#A3DAFF",
          "#045252",
          "#67E6BB",
          "#4E065C",
          "#BB80D1",
        ],
      }
    },
    dark: {
      ...defaultTheme.dark,
      // Dark mode: pure black background, grey placeholders, same artifact‑button green
      background: "#1A1A1A", // Dark background
      text: "#FFFFFF", // White text
      border: "#343434", // Dark border
      fontFamily: `"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif`,
      buttonHover: "#005EB8", // Dechra Blue
      optionsBg: "#2D2D2D", // Dark neutral background
      sidebarBg: "#1A1A1A", // Dark background
      optionsHover: "#005EB8", // Dechra Blue
      convoStarterBg: "#2D2D2D", // Dark neutral background
      convoStarterHover: "#005EB8", // Dechra Blue
      placeholder: "#AAAAAA", // Light gray placeholder
      svgFill: "#AAAAAA", // Light gray icons
      multiValueBg: "#333333", // Selected item background
      optionHoverBg: "#444444", // Hover background
      removeValueBg: "#555555", // Remove button background
      disabledBg: "#2A2A2A", // Disabled background
      disabledText: "#AAAAAA", // Disabled text
      disabledPlaceholder: "#888888", // Disabled placeholder
      disabledSvgFill: "#888888", // Disabled icon color
      chatBTNBackground: "#005EB8", // convoStarterHover
      chatBTNHover: "#444444", // optionHoverBg
      chatBTNDisabled: "#2A2A2A", // disabledBg
      chartColors: {
        desktop: [
          "#003B71",
          "#A3DAFF",
          "#045252",
          "#67E6BB",
          "#4E065C",
          "#BB80D1",
        ],
        mobile: [
          "#003B71",
          "#A3DAFF",
          "#045252",
          "#67E6BB",
          "#4E065C",
          "#BB80D1",
        ],
      }
    },
  },
  shell: {
    light: {
      ...defaultTheme.light,
      background: "#FFFFFF", // Dechra White
      text: "#333333", // Dechra Dark Gray
      border: "#EDEDED", // Dechra Gray
      fontFamily: "Verdana",
      buttonHover: "#818285", // Dechra Light Blue
      optionsBg: "#F5F5F5", // Light neutral background
      sidebarBg: "#F5F5F5", // Light neutral background
      optionsHover: "#818285", // Dechra Light Blue
      convoStarterBg: "#F5F5F5", // Light neutral background
      convoStarterHover: "#818285", // Dechra Light Blue
      placeholder: "#888888", // Neutral placeholder text
      svgFill: "#555555", // Icon color
      multiValueBg: "#E6E6E6", // Selected item background
      optionHoverBg: "#F5F5F5", // Hover background
      removeValueBg: "#D9D9D9", // Remove button background
      disabledBg: "#F0F0F0", // Disabled background
      disabledText: "#A0A0A0", // Disabled text
      disabledPlaceholder: "#BBBBBB", // Disabled placeholder
      disabledSvgFill: "#BBBBBB",
      chatBTNBackground: "#7ed0e0", // convoStarterHover
      chatBTNHover: "#019ac7", // optionHoverBg
      chatBTNDisabled: "#2A2A2A", // disabledBg
      chartColors: {
        desktop: [
          "#ffd203",
          "#ee3124",
          "#019ac7",
          "#c1d82f",
        ],
        mobile: [
          "#ffd203",
          "#ee3124",
          "#019ac7",
          "#c1d82f",
        ],
      }
    },
    dark: {
      ...defaultTheme.dark,
      // Dark mode: pure black background, grey placeholders, same artifact‑button green
      background: "#1A1A1A", // Dark background
      text: "#FFFFFF", // White text
      border: "#343434", // Dark border
      fontFamily: "Verdana",
      buttonHover: "#818285", // Dechra Blue
      optionsBg: "#2D2D2D", // Dark neutral background
      sidebarBg: "#1A1A1A", // Dark background
      optionsHover: "#818285", // Dechra Blue
      convoStarterBg: "#2D2D2D", // Dark neutral background
      convoStarterHover: "#818285", // Dechra Blue
      placeholder: "#AAAAAA", // Light gray placeholder
      svgFill: "#AAAAAA", // Light gray icons
      multiValueBg: "#333333", // Selected item background
      optionHoverBg: "#444444", // Hover background
      removeValueBg: "#555555", // Remove button background
      disabledBg: "#2A2A2A", // Disabled background
      disabledText: "#AAAAAA", // Disabled text
      disabledPlaceholder: "#888888", // Disabled placeholder
      disabledSvgFill: "#888888", // Disabled icon color
      chatBTNBackground: "#005596", // convoStarterHover
      chatBTNHover: "#019ac7", // optionHoverBg
      chatBTNDisabled: "#2A2A2A", // disabledBg
      chartColors: {
        desktop: [
          "#ffd203",
          "#ee3124",
          "#019ac7",
          "#c1d82f",
        ],
        mobile: [
          "#ffd203",
          "#ee3124",
          "#019ac7",
          "#c1d82f",
        ],
      }
    },
  },
};

// 4. Lookup helpers
export function getThemeColors(
  tenant: TenantKey,
  mode: ThemeMode
): ThemeColors {
  const set = TenantThemes[getTenantGroupName(tenant)] ?? TenantThemes.default;
  return set[mode];
}

function getTenantGroupName(tenant: TenantKey): string {
  if (isDechraFn(tenant)) {
    return "dechra";
  } else if (isShellFn(tenant)) {
    return "shell";
  }
  return tenant;
}

export function getChartColors(tenant: TenantKey, theme: ThemeMode): {
  desktop: string[];
  mobile: string[];
} {
  const set = TenantThemes[getTenantGroupName(tenant)] ?? TenantThemes.default;
  return set[theme].chartColors;
}

// Special color overrides for specific components (preserved for backward compatibility)
export const COMPONENT_COLORS = {
  chatPage: {
    dark: {
      background: "#000000", // Pure black for chat page
    },
    light: {
      background: "#ffffff", // Pure white for chat page
    },
  },
  sidebar: {
    // light: {
    //   buttonHover: "#89BDA0", // Different hover color for sidebar in light mode
    // },
  },
  header: {
    // background: "#2A5354", // Fixed header background
  },
  table: {
    light: {
      headerBackground: "#F0F0F0",
      border: "#DDDDDD",
    },
    dark: {
      headerBackground: "#191919",
      border: "#191919",
    },
  },
};

// Utility functions for component-specific colors
export const getComponentColors = (
  component: keyof typeof COMPONENT_COLORS,
  mode: ThemeMode
) => {
  const componentConfig = COMPONENT_COLORS[component];
  if (!componentConfig) return {};

  // Handle components that don't have theme-specific colors (like header)
  if ("background" in componentConfig) {
    return componentConfig;
  }

  // Handle theme-specific component colors
  return (componentConfig as any)[mode] || {};
};

// Backward compatibility exports (deprecated - use new structure)
export type Theme = ThemeMode;
export const CHART_COLORS = ChartColors.desktop;
export const CHART_COLORS_MOBILE = ChartColors.mobile;
export const THEME_COLORS = {
  light: defaultTheme.light,
  dark: defaultTheme.dark,
};
